import React, { useState, useRef, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'

import styles from './barChart.module.scss'

const MAX_BAR_HEIGHT = 100

export type ChartDatum = {
  label: string
  count: number
  onBarClick?: (e: React.MouseEvent, label: string) => void // If we want to provide a callback per bar
  tooltip?: React.ReactNode
  color?: string // Hex color
}

type Props = {
  data: ChartDatum[]
  onGlobalBarClick?: (e: React.MouseEvent, label: string) => void
  maxChartHeight?: number
  relativeMaximum?: number
  containerClassName?: string
  className?: string
  labelContainerClassName?: string
  style?: React.CSSProperties
}

const BarChart = ({
  data,
  onGlobalBarClick,
  maxChartHeight = MAX_BAR_HEIGHT,
  relativeMaximum,
  containerClassName = '',
  className = '',
  labelContainerClassName = '',
  style,
}: Props) => {
  const tallestBar = relativeMaximum || data.reduce((largest, { count }) => (count > largest ? count : largest), 0)
  const relativeHeights = data.map(({ count }) => count / tallestBar)

  return (
    <div className={`${styles.container} ${containerClassName}`} style={style}>
      <div className={`${styles.chart} ${className}`}>
        {data.map((bar, index) => {
          const relativeHeight = relativeHeights[index]

          return (
            <div className={styles.barContainer} key={bar.label}>
              {!!bar.count && <label className={styles.totalCount}>{bar.count}</label>}
              <Bar
                key={bar.label}
                bar={bar}
                onGlobalBarClick={onGlobalBarClick}
                maxChartHeight={maxChartHeight}
                relativeChartHeight={relativeHeight}
              />
            </div>
          )
        })}
      </div>

      <div className={styles.axis} />

      <div className={`${styles.key} ${labelContainerClassName}`}>
        {data.map(({ label }, index) => (
          <div className={styles.barContainer} key={label}>
            <label>{label}</label>
          </div>
        ))}
      </div>
    </div>
  )
}

export default BarChart

type BarProps = {
  bar: ChartDatum

  maxChartHeight: number
  relativeChartHeight: number

  onGlobalBarClick?: (e: React.MouseEvent, label: string) => void
}

const Bar = ({ bar, onGlobalBarClick, maxChartHeight, relativeChartHeight }: BarProps) => {
  const [hover, setHover] = useState(false)
  const [viewable, setViewable] = useState(false)

  const touched = useRef(false)
  const delayRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tooltipRef = useRef<any>(null)

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!tooltipRef.current) return

      const tooltipWidth = tooltipRef.current.clientWidth
      const tooltipHeight = tooltipRef.current.clientHeight

      const windowWidth = window.innerWidth || 0
      const windowHeight = window.innerHeight || 0

      let x = e.clientX
      let y = e.clientY

      if (x === 0 && y === 0) return

      const switchHoverToLeftOfCursor = e.clientX > windowWidth - tooltipWidth
      const switchHoverToTopOfCursor = e.clientY > windowHeight - tooltipHeight

      const xOffset = switchHoverToLeftOfCursor ? -1 * tooltipWidth - 25 : 25
      const yOffset = switchHoverToTopOfCursor ? y - tooltipHeight - 25 : y

      const transform = `translate(${x + xOffset}px, ${yOffset + 25}px)`

      tooltipRef.current.style.display = 'unset'
      tooltipRef.current.style.transform = transform
    },
    [hover],
  )

  const handleHoverStart = (e: React.MouseEvent) => {
    if (touched.current) return
    if (hover) return

    delayRef.current = setTimeout(() => {
      handleMouseMove(e.nativeEvent as MouseEvent) // Calling once directly because if the user exclusively scrolls with the mouse wheel, the mousemove event won't fire
      setViewable(true)
    }, 250)

    setHover(true)
  }

  const handleHoverEnd = () => {
    setViewable(false)

    setHover(false)
  }

  useEffect(() => {
    if (!hover) return () => document.removeEventListener('mousemove', handleMouseMove)

    document.addEventListener('mousemove', handleMouseMove, false)
  }, [hover])

  return (
    <>
      <button
        onMouseOver={handleHoverStart}
        onMouseLeave={handleHoverEnd}
        onTouchStart={() => (touched.current = true)}
        // Delay is to be sure that touches are still considered touch on tap
        onTouchEnd={() => setTimeout(() => (touched.current = false), 100)}
        className={styles.bar}
        onClick={e => {
          // NOTE - If onBarClick is provided, it will take precedence over onGlobalBarClick
          if (bar.onBarClick) return bar.onBarClick(e, bar.label)
          if (onGlobalBarClick) return onGlobalBarClick(e, bar.label)
        }}
        style={{
          height: `${maxChartHeight * relativeChartHeight}px`,
          display: bar.count > 0 ? undefined : 'none',
          backgroundColor: bar.color || undefined,
        }}></button>

      {hover &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            className={styles.tooltip}
            style={{ opacity: viewable && tooltipRef?.current?.style?.transform ? 1 : 0 }}>
            {bar.tooltip || bar.count}
          </div>,
          document.body,
        )}
    </>
  )
}
